import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Breadcrumb from '../components/Breadcrumb';
import Footer from '../components/Footer';
import {
  BRAND_NAME,
  COMPLIANCE_EMAIL,
  MAILING_ADDRESS_LINES,
  GOVERNING_LAW_STATE,
} from '../config/legal';

const breadcrumbItems = [
  { label: 'Home', href: '/' },
  { label: 'Terms & Conditions', href: '/terms-and-conditions', current: true },
];

const TermsAndConditionsPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#1F3B60' }}>
      <Helmet>
        <title>Terms &amp; Conditions | {BRAND_NAME}</title>
        <meta name="description" content={`Terms and Conditions for ${BRAND_NAME}.`} />
        <link rel="canonical" href="https://pacificcrestprop.com/terms-and-conditions" />
        <meta property="og:title" content={`Terms & Conditions | ${BRAND_NAME}`} />
        <meta property="og:description" content={`Terms and Conditions for ${BRAND_NAME}.`} />
        <meta property="og:url" content="https://pacificcrestprop.com/terms-and-conditions" />
      </Helmet>

      <Header />
      <div className="pt-20">
        <Breadcrumb items={breadcrumbItems} variant="dark" />
      </div>

      {/* Hero Section */}
      <section className="pt-8 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-display font-bold text-4xl sm:text-5xl text-white mb-6">
            Terms &amp; Conditions &ndash; {BRAND_NAME}
          </h1>
          <div className="divider-gold mx-auto mb-8" />
          <p className="text-lg text-white/70">Effective Date: February 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F4F1E9' }}>
        <div className="max-w-4xl mx-auto py-16">
          <div className="prose prose-lg max-w-none" style={{ color: '#1F3B60' }}>

            <p>
              These Terms &amp; Conditions ("Terms") govern your use of this website and any forms submitted through our domains.
            </p>
            <p>By accessing this website or submitting a form, you agree to these Terms.</p>

            {/* 1 */}
            <h2 className="font-display font-bold text-2xl mt-10 mb-4" style={{ color: '#1F3B60' }}>
              1. Website Use
            </h2>
            <p>
              You agree to use this website for lawful purposes and to provide accurate information when submitting forms.
            </p>

            {/* 2 */}
            <h2 className="font-display font-bold text-2xl mt-10 mb-4" style={{ color: '#1F3B60' }}>
              2. No Agency Relationship
            </h2>
            <p>
              {BRAND_NAME} is a real estate investment and services business. We are not acting as your real estate agent unless a separate written agreement is executed.
            </p>
            <p>Submitting a form does not create an agency relationship.</p>

            {/* 3 */}
            <h2 className="font-display font-bold text-2xl mt-10 mb-4" style={{ color: '#1F3B60' }}>
              3. No Guarantee of Offer
            </h2>
            <p>
              Any potential offer or renovation quote is subject to verification, inspection, underwriting, and due diligence. No offer is binding unless set forth in a signed written agreement.
            </p>

            {/* 4 */}
            <h2 className="font-display font-bold text-2xl mt-10 mb-4" style={{ color: '#1F3B60' }}>
              4. Communications &amp; SMS Terms
            </h2>
            <p>
              If you check the SMS consent box, you authorize {BRAND_NAME} to contact you regarding your inquiry, including via automated technology where permitted by law.
            </p>
            <p>Consent is not a condition of purchase.</p>
            <p>Message frequency varies.</p>
            <p>Message and data rates may apply.</p>
            <p>Reply STOP to opt out.</p>
            <p>Reply HELP for assistance.</p>
            <p>You represent that you are the subscriber or authorized user of the phone number provided.</p>

            {/* 5 */}
            <h2 className="font-display font-bold text-2xl mt-10 mb-4" style={{ color: '#1F3B60' }}>
              5. Intellectual Property
            </h2>
            <p>
              All website content, branding, logos, and materials are the property of {BRAND_NAME} and may not be reproduced without permission.
            </p>

            {/* 6 */}
            <h2 className="font-display font-bold text-2xl mt-10 mb-4" style={{ color: '#1F3B60' }}>
              6. Disclaimer
            </h2>
            <p>Website content is provided "as is" without warranties of any kind.</p>

            {/* 7 */}
            <h2 className="font-display font-bold text-2xl mt-10 mb-4" style={{ color: '#1F3B60' }}>
              7. Limitation of Liability
            </h2>
            <p>
              To the maximum extent permitted by law, {BRAND_NAME} is not liable for indirect or consequential damages arising from use of this website.
            </p>

            {/* 8 */}
            <h2 className="font-display font-bold text-2xl mt-10 mb-4" style={{ color: '#1F3B60' }}>
              8. Third-Party Services
            </h2>
            <p>
              We may use third-party providers for communications, CRM systems, hosting, and analytics. Your use of third-party services may be subject to their terms.
            </p>

            {/* 9 */}
            <h2 className="font-display font-bold text-2xl mt-10 mb-4" style={{ color: '#1F3B60' }}>
              9. Governing Law
            </h2>
            <p>These Terms are governed by the laws of the State of {GOVERNING_LAW_STATE}.</p>

            {/* 10 */}
            <h2 className="font-display font-bold text-2xl mt-10 mb-4" style={{ color: '#1F3B60' }}>
              10. Contact Information
            </h2>
            <p>{BRAND_NAME}</p>
            <div className="mt-4">
              {MAILING_ADDRESS_LINES.map((line, i) => (
                <p key={i} className="my-0">{line}</p>
              ))}
            </div>
            <p className="mt-4">
              <a href={`mailto:${COMPLIANCE_EMAIL}`} className="underline" style={{ color: '#D6A94F' }}>
                {COMPLIANCE_EMAIL}
              </a>
            </p>
            <p>
              <a href="https://pacificcrestprop.com" className="underline" style={{ color: '#D6A94F' }}>
                https://pacificcrestprop.com
              </a>
            </p>

            {/* 11 */}
            <h2 className="font-display font-bold text-2xl mt-10 mb-4" style={{ color: '#1F3B60' }}>
              11. Updates
            </h2>
            <p>
              We reserve the right to modify these Terms at any time. Updates will be posted here with a revised effective date.
            </p>

            {/* 12 */}
            <h2 className="font-display font-bold text-2xl mt-10 mb-4" style={{ color: '#1F3B60' }}>
              12. SMS Terms &amp; Conditions
            </h2>

            <h3 className="font-display font-semibold text-xl mt-6 mb-3" style={{ color: '#1F3B60' }}>
              Product Description
            </h3>
            <p>
              By providing your mobile phone number, you consent to receive SMS messages from {BRAND_NAME} related to real estate acquisition and renovation.
            </p>
            <p>Message frequency may vary.</p>
            <p>
              You will only receive SMS as part of discussions with our representatives and not as part of any promotional campaigns.
            </p>

            <h3 className="font-display font-semibold text-xl mt-6 mb-3" style={{ color: '#1F3B60' }}>
              Message and Data Rates
            </h3>
            <p>Standard message and data rates may apply.</p>

            <h3 className="font-display font-semibold text-xl mt-6 mb-3" style={{ color: '#1F3B60' }}>
              Opting Out
            </h3>
            <p>
              You may opt out of receiving SMS messages at any time by replying with "STOP" to any SMS message you receive from us. After opting out, you will receive a confirmation message, and we will cease sending SMS messages to your number.
            </p>

            <h3 className="font-display font-semibold text-xl mt-6 mb-3" style={{ color: '#1F3B60' }}>
              Help and Support
            </h3>
            <p>
              If you need assistance or have questions about our SMS service, reply with "HELP" to any SMS message you receive, or contact our customer support team at{' '}
              <a href={`mailto:${COMPLIANCE_EMAIL}`} className="underline" style={{ color: '#D6A94F' }}>
                {COMPLIANCE_EMAIL}
              </a>.
            </p>

            <h3 className="font-display font-semibold text-xl mt-6 mb-3" style={{ color: '#1F3B60' }}>
              Privacy Policy
            </h3>
            <p>
              Your phone number will be handled in accordance with our{' '}
              <a href="https://pacificcrestprop.com/privacy-policy" className="underline" style={{ color: '#D6A94F' }}>
                Privacy Policy
              </a>. No mobile information will be shared with third parties/affiliates for marketing/promotional purposes. All the above categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.
            </p>
          </div>
        </div>
      </section>

      {/* Back to Home */}
      <section className="py-12 px-4 text-center" style={{ backgroundColor: '#1F3B60' }}>
        <Link
          to="/"
          className="inline-flex items-center space-x-2 font-medium text-white/60 hover:text-white transition-colors duration-300"
        >
          <ArrowRight className="w-4 h-4 rotate-180" />
          <span>Back to Home</span>
        </Link>
      </section>

      <Footer />
    </div>
  );
};

export default TermsAndConditionsPage;
